/**
 * Calculate the distance between two points on Earth using the Haversine formula
 * @param lat1 Latitude of first point
 * @param lng1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lng2 Longitude of second point
 * @returns Distance in miles
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
	// Validate input coordinates
	if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) {
		throw new Error('Coordinates cannot be null or undefined');
	}
	
	if (isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)) {
		throw new Error('Coordinates must be valid numbers');
	}
	
	// Validate coordinate bounds
	if (lat1 < -90 || lat1 > 90 || lat2 < -90 || lat2 > 90) {
		throw new Error('Latitude must be between -90 and 90 degrees');
	}
	
	if (lng1 < -180 || lng1 > 180 || lng2 < -180 || lng2 > 180) {
		throw new Error('Longitude must be between -180 and 180 degrees');
	}

	const R = 3959; // Radius of Earth in miles
	const dLat = toRadians(lat2 - lat1);
	const dLng = toRadians(lng2 - lng1);
	const a
		= Math.sin(dLat / 2) * Math.sin(dLat / 2)
		+ Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2))
		* Math.sin(dLng / 2) * Math.sin(dLng / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
	return degrees * (Math.PI / 180);
}

/**
 * Format distance for display
 * @param distance Distance in miles
 * @returns Formatted string (e.g., "1.2mi", "0.5mi")
 */
export function formatDistance(distance: number): string {
	// Validate input
	if (distance == null || isNaN(distance)) {
		return 'Invalid distance';
	}
	
	// Handle negative distances
	if (distance < 0) {
		return `${distance.toFixed(1)}mi`; // Return as-is for now, could throw error instead
	}
	
	// Handle infinity
	if (!isFinite(distance)) {
		return 'Infinity';
	}
	
	if (distance < 0.1) {
		return '<0.1mi';
	}
	return `${distance.toFixed(1)}mi`;
}
